import React, { useEffect, useState, useRef, useCallback, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Image from 'next/image';
import rehypeRaw from 'rehype-raw';
import { Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { ArchiveIcon, CheckIcon, InfoIcon, MagicWandIcon, ListPlusIcon, CopyIcon, CaretCircleUpDownIcon, ThumbsUpIcon, ThumbsDownIcon, FunnelIcon, TranslateIcon, CaretDownIcon, FilePdfIcon } from "@phosphor-icons/react";
import { Spinner } from '@heroui/spinner';
import { IsARefusal, CaptureError, getDownloadUrl, getMediaType, recordStat, extractPathAfterBulk, getUiSourceFromChatSource } from '@/shared/functions';
import { Tooltip } from "@heroui/react";
import { useWindowSize } from '../shared/clientFunctions';
import { FetchWithVersionCheck, trackEvent } from '@/shared/functions';
import { FeatureFlagTypes, MediaObject, MediaType, ScopesObject, Slide, SourceObject } from '@/shared/types';
import { REFUSAL_MESSAGES, PASS_MESSAGES, SMALL_VIEWPORT_WIDTH } from '@/shared/constants';
import { LLMConfig, Source } from '../shared/types';
import { Card, CardHeader, CardBody, CardFooter } from "@heroui/card";
import { StyledList, StyledListItem } from './ui/styledListMarkdown';
import { AuthContext, FullScreenContext, StatsContext } from '@/app/contexts';
import { HotKeys } from '@/components/hot-keys';
import * as Sentry from "@sentry/nextjs";
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/modal';
import { Button } from '@heroui/react';
import EventsReviewModal from './events-review-modal';
import { SourceCard } from './sourceCard';
import { useMediaSourceShare } from './mediaSourceShare';
import { dispatchCopyToHistory } from './recently-copied';
const FETCHING_MESSAGE = 'Retrieving answer from chat history...';
interface AnswerItemProps {
  userId: string;
  caseId: string;
  answerIndex: number;
  question: string;
  answerText: string;
  sources: Source[];
  epochTimeSecs: number;
  onRedo: (question: string) => void;
  addToTimeline: (event: EventFromChat) => void;
  addMultipleToTimeline: (events: Slide[], useEnrichment: boolean, edit: boolean) => void;
  isEditableTimeline: boolean;
  featureFlags: {
    [key: string]: FeatureFlagTypes;
  };
  resultForFeedback: any;
  scopesObject: ScopesObject;
  setCurrentScopesAndFiles: ({
    scopes,
    files,
    excludedScopes,
    excludedFiles
  }: ScopesObject) => void;
  addSourceToScope: (path: string) => void;
  onSendToQuery?: (text: string) => void;
  onFilterEvidence?: (text: string) => void;
  setIsExpanded?: React.Dispatch<React.SetStateAction<boolean>>;
  extractionJob: any;
  createEventsFromEntityEvents: (jobId: string, events: any[], enriching: boolean, timelineIdIn?: string) => Promise<void>;
  getEventsFromEntityService: (action: string, timelineId: string | null, caseId: string, answerItemText: string, sources: Source[], epochTimeSecs: number, jobId: string | null, cancelPolling: boolean) => Promise<void>;
  snapToTop: () => void;
  handleExportAnswerAsPdf: (epochTimeSecs: number, answerElement: HTMLDivElement) => void;
  isExporting: boolean;
}
export interface Answer {
  question: string;
  answerText: string;
  sources: Source[];
  sessionId: string | null;
  timeSaved: number;
  resultForFeedback?: any;
  scopesObject: ScopesObject;
  epochTimeSecs: number;
}
;
export interface EventFromChat {
  answerText: string;
  sources: Source[];
  autogen: boolean;
  epochTimeSecs: number;
  slideTitle?: string;
  slideDescription?: string;
  media?: MediaObject[];
}
function getFilename(source: Source): string {
  const filename = source.sourceFilePath?.split('/').pop() || '';
  return filename;
}

/** Only audio/video use time indices in source labels; start/end are ignored for all other types. */
function audioVideoTimeSuffix(source: Source): string {
  const parts: string[] = [];
  if (source.sourceContext?.startTime) parts.push(`Time Index: ${source.sourceContext.startTime}`);
  if (source.sourceContext?.endTime) parts.push(`End: ${source.sourceContext.endTime}`);
  return parts.length ? `, ${parts.join(', ')}` : '';
}

/**
 * When the sources panel is open, {@link sourceMediaType} is known. When closed, we only call
 * {@link getMediaType} if page or time fields are set so labels stay cheap for typical rows.
 */
function sourceListLabelParts(source: Source, sourceMediaType: MediaType | null): {
  showPageNumber: boolean;
  timeSuffix: string;
} {
  if (sourceMediaType !== null) {
    const isAv = sourceMediaType === 'audio' || sourceMediaType === 'video';
    let showPageNumber = false;
    if (!isAv && source.page !== null && source.page !== undefined) {
      showPageNumber = true;
      if (source.previewFilePath !== '' && source.previewFilePath !== source.sourceFilePath) {
        showPageNumber = false;
      }
    }
    const timeSuffix = isAv ? audioVideoTimeSuffix(source) : '';
    return {
      showPageNumber,
      timeSuffix
    };
  }
  // Panel closed: infer type only when page or time fields could affect the label.
  const needInferredMediaType = source.page !== null && source.page !== undefined || !!source.sourceContext?.startTime || !!source.sourceContext?.endTime;
  const inferredMediaType = needInferredMediaType ? getMediaType(source, source.sourceInfo) : null;
  if (inferredMediaType === 'audio' || inferredMediaType === 'video') {
    return {
      showPageNumber: false,
      timeSuffix: audioVideoTimeSuffix(source)
    };
  }
  let showPageNumber = source.page !== null && source.page !== undefined;
  if (showPageNumber && source.previewFilePath !== '' && source.previewFilePath !== source.sourceFilePath) {
    showPageNumber = false;
  }
  return {
    showPageNumber,
    timeSuffix: ''
  };
}
async function handleCopyToClipboard(question: string, answerIndex: number, sources: Source[], answerText: string): Promise<boolean> {
  const answer = document.getElementById(`answerText_${answerIndex}`);
  if (!!answer && !!question) {
    let html = `<h3>Question: ${question}</h3>${answer.innerHTML}`;
    let plain = `Question: ${question}\n\n${answerText}`;
    if (sources && sources.length > 0) {
      html += '<h3>Sources:</h3><ol>';
      plain += '\n\nSources:\n';
      for (let i = 0; i < sources.length; i++) {
        const source = sources[i];
        const path = source.truncatedFilePath;
        const sourceMediaType = getMediaType(source, source.sourceInfo);
        const isAv = sourceMediaType === 'audio' || sourceMediaType === 'video';
        const page = !isAv && source.page ? ', page: ' + source.page : '';
        const timePart = isAv ? audioVideoTimeSuffix(source) : '';
        html += `<li>${path}${page}${timePart}</li>`;
        plain += `${i + 1}. ${path}${page}${timePart}\n`;
      }
      html += '</ol>';
    }
    ;
    try {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([html], {
          type: 'text/html'
        }),
        'text/plain': new Blob([plain], {
          type: 'text/plain'
        })
      });
      await navigator.clipboard.write([clipboardItem]).then(() => {
        dispatchCopyToHistory(plain);
        return Promise.resolve(true);
      }).catch(error => {
        return Promise.resolve(false);
      });
    } catch (error) {
      return Promise.resolve(false);
    }
  }
  return Promise.resolve(true);
}
interface ContextMenuPosition {
  x: number;
  y: number;
}
const languageOptions = [{
  code: 'en',
  name: 'English',
  englishName: 'English'
}, {
  code: 'el',
  name: 'Ελληνικά',
  englishName: 'Greek'
}, {
  code: 'ar',
  name: 'العربية',
  englishName: 'Arabic'
}, {
  code: 'zh',
  name: '简体中文',
  englishName: 'Chinese (Simplified)'
}, {
  code: 'hi',
  name: 'हिन्दी',
  englishName: 'Hindi'
}, {
  code: 'ru',
  name: 'Русский',
  englishName: 'Russian'
}, {
  code: 'es',
  name: 'Español',
  englishName: 'Spanish'
}, {
  code: 'de',
  name: 'Deutsch',
  englishName: 'German'
}];
export const AnswerItem: React.FC<AnswerItemProps> = ({
  userId,
  caseId,
  answerIndex,
  question,
  answerText,
  sources,
  epochTimeSecs,
  onRedo,
  addToTimeline,
  addMultipleToTimeline,
  isEditableTimeline,
  featureFlags,
  resultForFeedback,
  scopesObject,
  setCurrentScopesAndFiles,
  addSourceToScope,
  onSendToQuery,
  onFilterEvidence,
  setIsExpanded,
  extractionJob,
  createEventsFromEntityEvents,
  getEventsFromEntityService,
  snapToTop,
  handleExportAnswerAsPdf,
  isExporting
}) => {
  const {
    width
  } = useWindowSize();
  const isSmallScreen = width !== undefined && width < SMALL_VIEWPORT_WIDTH;
  const mediaSourceShare = useMediaSourceShare();
  const {
    setFullscreenMediaItem
  } = useContext(FullScreenContext);
  const [sourcesMediaOpen, setSourcesMediaOpen] = useState(false);
  const [selectedSourceIndex, setSelectedSourceIndex] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedSource, setCopiedSource] = useState(-1);
  const [redoClicked, setRedoClicked] = useState(false);
  const [refusalMessages, setRefusalMessages] = useState<string[]>(REFUSAL_MESSAGES);
  const [passMessages, setPassMessages] = useState<string[]>(PASS_MESSAGES);
  const [leftCardWidth, setLeftCardWidth] = useState<number | null>(null);
  const [rightCardWidth, setRightCardWidth] = useState<number | null>(null);
  const [positiveFeedbackClicked, setPositiveFeedbackClicked] = useState(false);
  const [negativeFeedbackClicked, setNegativeFeedbackClicked] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState('');
  const [dataForChatResultFeedback, setDataForChatResultFeedback] = useState<any>(null);
  const [applyScopesClicked, setApplyScopesClicked] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltip3Open, setTooltip3Open] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuPosition | null>(null);
  const [selectedText, setSelectedText] = useState('');
  const [addedSource, setAddedSource] = useState(-1);
  const [fullScreenSource, setFullScreenSource] = useState<Source>();
  const [multipleEventsPopoverVisible, setMultipleEventsPopoverVisible] = useState(false);
  const [wandDisabled, setWandDisabled] = useState(false);
  const [canApplyJob, setCanApplyJob] = useState(false);
  const [applyCardOpen, setApplyCardOpen] = useState(false);
  const [timelineReady, setTimelineReady] = useState(isEditableTimeline);
  const [extractionJobStatus, setExtractionJobStatus] = useState<string | null>(null);
  const [translateDropdownOpen, setTranslateDropdownOpen] = useState(false);
  const [answerTextLocal, setAnswerTextLocal] = useState<string | null>(null);
  const [pollForChatHistory, setPollForChatHistory] = useState<number>(0);
  const [sourcesLocal, setSourcesLocal] = useState(sources);
  const selectionRef = useRef<Selection | null>(null);
  const rangeRef = useRef<Range | null>(null);
  const isSelectingRef = useRef(false);
  const highlightRef = useRef<SVGSVGElement | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const answerItemRef = useRef<HTMLDivElement>(null);
  const answerItemMarkdownRef = useRef<HTMLDivElement>(null);
  const answerPollerRef = useRef<NodeJS.Timeout | null>(null);
  const responseCardRef = useRef<HTMLDivElement>(null);
  const sourcesCardParentRef = useRef<HTMLDivElement>(null);
  const sourcesNavRef = useRef<HTMLDivElement>(null);
  const preliminaryMessagesRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const leftCardWidthRef = useRef<number | null>(null);
  const rightCardWidthRef = useRef<number | null>(null);
  const statsContext = useContext(StatsContext);
  const {
    idToken,
    accessToken,
    tenantName
  } = useContext(AuthContext);
  const questionId = `question_${answerIndex}`;

  // needed ?
  const fixedWidthCards = {
    answer: false,
    sources: false
  };
  const [showSources, setShowSources] = useState(!isSmallScreen);
  const [answerCardHeight, setAnswerCardHeight] = useState<number | 'auto'>('auto');
  const [sourcesCardWidth, setSourcesCardWidth] = useState<string>('auto');
  // needed ?

  const localSnapToTop = () => {
    if (isSmallScreen) {
      return;
    }
    snapToTop();
  };
  const startPollingAnswerItem = (answerTextString: string) => {
    if (answerTextString.startsWith('PRELIMINARY_MESSAGES|') && new Date().getTime() / 1000 - epochTimeSecs < 1800) {
      if (answerPollerRef.current) {
        clearTimeout(answerPollerRef.current);
      }
      const pollingDelay = new Date().getTime() / 1000 - epochTimeSecs < 15 ? 3000 : 10000;
      answerPollerRef.current = setTimeout(() => {
        setPollForChatHistory(new Date().getTime());
      }, pollingDelay);
    }
  };

  // Clear polling timeout on unmount to avoid setState on unmounted component and memory leaks
  useEffect(() => {
    return () => {
      if (answerPollerRef.current) {
        clearTimeout(answerPollerRef.current);
        answerPollerRef.current = null;
      }
    };
  }, []);
  const getAnswerFromChatHistory = async () => {
    try {
      const historyResponse = await FetchWithVersionCheck('/api/protected/chat/history', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          userId: userId,
          caseId: caseId,
          epochTimeSecs: epochTimeSecs
        })
      });
      const history = await historyResponse.json();
      if (history[0]?.answer) {
        const currentItem = history[0];
        const answer = JSON.parse(currentItem.answer);
        const outputStr = answer?.output;
        let newAnswerText = typeof outputStr === 'string' && outputStr.trim() !== '' ? outputStr : 'Processing...';
        const status = 'status' in currentItem ? currentItem.status : 'unknown';
        const isInitializing = status === 'initializing';
        const isInProgress = status === 'inprogress';
        const isCompleted = status === 'completed';
        const hasError = status === 'error';
        const isPrelimary = 'is_preliminary' in answer && answer.is_preliminary === true;
        const containsMessages = !!answer && 'messages' in answer && answer.messages.length > 0 ? true : false;
        const isPreliminaryResult = isInitializing || isInProgress || isPrelimary;
        const isFinalResult = isCompleted || containsMessages && !isPreliminaryResult || !containsMessages;
        if (hasError) {
          newAnswerText = outputStr && outputStr.trim() !== '' ? outputStr : 'An error occurred.';
        } else if (isInitializing) {
          newAnswerText = 'PRELIMINARY_MESSAGES|Initializing...';
        } else if (isPreliminaryResult && containsMessages) {
          newAnswerText = `PRELIMINARY_MESSAGES|${answer.messages.map((message: any) => message.content).join('\n')}`;
        } else if (isPreliminaryResult && newAnswerText) {
          newAnswerText = `PRELIMINARY_MESSAGES|${newAnswerText}`;
        }
        setAnswerTextLocal(newAnswerText);
        if (isPreliminaryResult || !isFinalResult || isInitializing) {
          startPollingAnswerItem(newAnswerText);
        }
        const newSourcesLocal: SourceObject[] = [];
        answer.citations?.forEach(async (source: Source) => {
          const sourceObject = getUiSourceFromChatSource(source);
          newSourcesLocal.push(sourceObject);
        });
        setSourcesLocal(newSourcesLocal);
      }
    } catch (error) {
      startPollingAnswerItem('PRELIMINARY_MESSAGES|Initializing...');
    }
  };
  useEffect(() => {
    if (answerTextLocal && answerTextLocal.startsWith('PRELIMINARY_MESSAGES|') && preliminaryMessagesRef.current) {
      if (answerTextLocal.split('\n').length > 1) {
        preliminaryMessagesRef.current.scrollTop = preliminaryMessagesRef.current.scrollHeight - 20;
      } else {
        preliminaryMessagesRef.current.scrollTop = 0;
      }
    }
  }, [answerTextLocal]);
  useEffect(() => {
    getAnswerFromChatHistory();
  }, [pollForChatHistory, answerText, sources]);
  useEffect(() => {
    setTimelineReady(isEditableTimeline);
  }, [isEditableTimeline]);
  useEffect(() => {
    if (extractionJob?.status === 'pending') {
      setWandDisabled(true);
      setCanApplyJob(false);
      setExtractionJobStatus('pending');
    } else if (extractionJob?.status === 'completed') {
      setWandDisabled(false);
      setCanApplyJob(true);
      setExtractionJobStatus('completed');
    } else {
      setWandDisabled(false);
      setCanApplyJob(false);
      setExtractionJobStatus(null);
    }
  }, [extractionJob, extractionJob?.status]);
  useEffect(() => {
    setPositiveFeedbackClicked(false);
    setNegativeFeedbackClicked(false);
    setShowFeedbackModal(false);
    setFeedbackSubmitted(false);
    setFeedbackReason('');
  }, [resultForFeedback, caseId, answerIndex, question, answerTextLocal, sourcesLocal]);
  const handleFeedbackClick = (direction: 'positive' | 'negative') => {
    setFeedbackReason('');
    if (resultForFeedback?.chat_key) {
      if (direction === 'positive') {
        setPositiveFeedbackClicked(true);
        setShowFeedbackModal(true);
      } else {
        setNegativeFeedbackClicked(true);
        setShowFeedbackModal(true);
      }
      const dataForChatResultFeedback = {
        "x_org_id": featureFlags?.orgId,
        "x_short_org_id": (featureFlags?.orgId as string || '').slice(0, 8),
        "x_epoch_time_ms_start": resultForFeedback.epoch_time_ms_start,
        "x_epoch_time_ms_end": resultForFeedback.epoch_time_ms_end,
        "x_timestamp_start": resultForFeedback.epoch_time_ms_start ? new Date(resultForFeedback.epoch_time_ms_start).toISOString() : null,
        "x_timestamp_end": resultForFeedback.epoch_time_ms_end ? new Date(resultForFeedback.epoch_time_ms_end).toISOString() : null,
        "chat_key": resultForFeedback.chat_key,
        "epoch_time_secs": resultForFeedback.epoch_time_secs,
        "answer": resultForFeedback.answer,
        "case_id": resultForFeedback.case_id,
        "model_arn": resultForFeedback.model_arn,
        "question": resultForFeedback.question,
        "session_id": resultForFeedback.session_id,
        "timestamp": resultForFeedback.timestamp,
        "time_saved": resultForFeedback.time_saved,
        "user_id": resultForFeedback.user_id
      };
      setDataForChatResultFeedback(dataForChatResultFeedback);
    }
  };
  const cancelChatResultFeedback = async () => {
    setPositiveFeedbackClicked(false);
    setNegativeFeedbackClicked(false);
    setShowFeedbackModal(false);
  };
  const sendChatResultFeedback = () => {
    const extrasForFeedback = dataForChatResultFeedback;
    extrasForFeedback.feedbackReason = feedbackReason;
    if (!featureFlags?.useIntercom) {
      Sentry.captureMessage(`Chat result ${positiveFeedbackClicked ? 'positive' : 'negative'} feedback`, {
        user: {
          username: resultForFeedback.userName,
          email: resultForFeedback.userId,
          id: resultForFeedback.userId
        },
        level: 'info',
        extra: extrasForFeedback
      });
    } else if (featureFlags?.useIntercom) {
      const eventName = positiveFeedbackClicked ? 'chat_result_positive_feedback' : 'chat_result_negative_feedback';
      const intercomEventData = {
        model_arn: dataForChatResultFeedback.model_arn,
        session_id: dataForChatResultFeedback.session_id,
        timestamp: dataForChatResultFeedback.timestamp,
        user: resultForFeedback.userId
      };
      const inhouseEventData = {
        ...intercomEventData,
        reason: feedbackReason,
        question: dataForChatResultFeedback.question,
        answer: dataForChatResultFeedback.answer,
        case_id: dataForChatResultFeedback.case_id
      };
      // Fire-and-forget: trackEvent POSTs to /api/protected/intercom; do not block UI dismissal.
      void trackEvent(eventName, inhouseEventData);
    }
    setPositiveFeedbackClicked(false);
    setNegativeFeedbackClicked(false);
    setShowFeedbackModal(false);
    setFeedbackSubmitted(true);
  };
  useEffect(() => {
    const refusalMessagesFromFeatureFlag: any = featureFlags['refusalMessages'];
    const passMessagesFromFeatureFlag: any = featureFlags['passMessages'];
    const llmConfigFromFeatureFlag = featureFlags['llmConfig'];
    const llmConfigRefusalMessages = (llmConfigFromFeatureFlag as unknown as LLMConfig)?.refusalMessages;
    const llmConfigPassMessages = (llmConfigFromFeatureFlag as unknown as LLMConfig)?.passMessages;
    const refusalMessagesArray = refusalMessagesFromFeatureFlag?.refusalMessages && Array.isArray(refusalMessagesFromFeatureFlag.refusalMessages) ? refusalMessagesFromFeatureFlag.refusalMessages : [];
    const passMessagesArray = passMessagesFromFeatureFlag?.passMessages && Array.isArray(passMessagesFromFeatureFlag.passMessages) ? passMessagesFromFeatureFlag.passMessages : [];
    const llmConfigRefusalMessagesArray = Array.isArray(llmConfigRefusalMessages) ? llmConfigRefusalMessages : [];
    const llmConfigPassMessagesArray = Array.isArray(llmConfigPassMessages) ? llmConfigPassMessages : [];
    if (llmConfigRefusalMessagesArray.length) {
      setRefusalMessages(llmConfigRefusalMessagesArray);
    } else if (refusalMessagesArray.length) {
      setRefusalMessages(refusalMessagesArray);
    } else {
      setRefusalMessages(REFUSAL_MESSAGES);
    }
    if (llmConfigPassMessagesArray.length) {
      setPassMessages(llmConfigPassMessagesArray);
    } else if (passMessagesArray.length) {
      setPassMessages(passMessagesArray);
    } else {
      setPassMessages(PASS_MESSAGES);
    }
  }, [featureFlags]);

  // Check if the answerText is a refusal
  const isRefusal = IsARefusal(answerTextLocal || '', refusalMessages, passMessages, sourcesLocal);
  const openSources = (sourceIndex: number) => {
    setSelectedSourceIndex(sourceIndex);
    setSourcesMediaOpen(true);
  };
  const closeSources = () => {
    setSourcesMediaOpen(false);
    setSelectedSourceIndex(null);
    // Reset the width of responseCardRef
    setLeftCardWidth(null); // Reset to default or initial width
  };
  const handleCopyClick = async (question: string, answerIndex: number, sources: Source[], answerTextCopy: string) => {
    const copied = await handleCopyToClipboard(question, answerIndex, sources, answerTextCopy);
    setCopied(copied);
  };
  const handleAddSourceToScope = async (source: Source, index: number) => {
    const path = source.sourceFilePath;
    if (path) {
      addSourceToScope(path);
      setAddedSource(index);
    }
  };
  useEffect(() => {
    if (addedSource !== -1) {
      setTimeout(() => {
        setAddedSource(-1);
      }, 1500);
    }
  }, [addedSource]);
  const handleCopySourceForTimeline = async (source: Source, index: number) => {
    try {
      const pathToCopy = extractPathAfterBulk(source.sourceFilePath || source.truncatedFilePath || '').truncatedFilePath;
      source.sourceContext = {
        ...source.sourceContext,
        transcriptUrl: source.location,
        transcriptPosition: 'below',
        showTranscriptMarkers: true,
        showTranscript: true,
        pageNumber: source.page || 1,
        startTime: source.sourceContext?.startTime || 0,
        previewUrl: source.previewFilePath || source.derived_object_url
      };
      const sourceToCopy = {
        source,
        caseId: caseId
      };
      if (pathToCopy) {
        await navigator.clipboard.writeText(pathToCopy);
        sessionStorage.setItem('source', JSON.stringify(sourceToCopy));
        setCopiedSource(index);
      }
    } catch (error) {
      console.error('Failed to copy source path:', error);
    }
  };
  useEffect(() => {
    if (copiedSource !== -1) {
      setTimeout(() => {
        setCopiedSource(-1);
      }, 1500);
    }
  }, [copiedSource]);
  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 1500);
    }
  }, [copied]);
  const handleDownload = async (source: Source) => {
    try {
      const url = await getDownloadUrl(source);
      if (url.error) {
        const errorMessage = `Error downloading document: ${url.error}`;
        CaptureError(errorMessage, url.error as Error, !!featureFlags?.useIntercom);
        alert(errorMessage);
        // TODO: Handle error (e.g., show an error message to the user)
      } else {
        window.open(url.signedUrl, '_blank');
      }
    } catch (error) {
      const errorMessage = `Error downloading document: ${error}`;
      CaptureError(errorMessage, error as Error, !!featureFlags?.useIntercom);
      alert(errorMessage);
      // TODO: Handle error (e.g., show an error message to the user)
    }
  };
  const handleRedoClick = () => {
    setRedoClicked(true);
    setTooltipOpen(true);
    onRedo(question);
    setTimeout(() => {
      setRedoClicked(false);
      setTooltipOpen(false);
    }, 1500);
  };
  const handleApplyScopesClick = () => {
    setApplyScopesClicked(true);
    setTooltip3Open(true);
    setCurrentScopesAndFiles(scopesObject);
    setTimeout(() => {
      setApplyScopesClicked(false);
      setTooltip3Open(false);
    }, 1500);
  };
  useEffect(() => {
    setShowSources(!isSmallScreen);
  }, [isSmallScreen]);
  useEffect(() => {
    if (responseCardRef.current && !isSmallScreen) {
      setAnswerCardHeight(responseCardRef.current.offsetHeight);
    }
  }, []);
  useEffect(() => {
    if (!isSmallScreen && sourcesMediaOpen) {
      setSourcesCardWidth('600px');
    } else {
      setSourcesCardWidth('fit-content');
      setRightCardWidth(null);
    }
  }, [sourcesMediaOpen, isSmallScreen]);
  const updateSourcesNavMaxHeight = () => {
    if (responseCardRef.current && sourcesCardParentRef.current && sourcesNavRef.current && !isSmallScreen) {
      const answerCardHeight = responseCardRef.current.offsetHeight;
      const sourcesCardParentHeight = sourcesCardParentRef.current.offsetHeight;
      const maxHeight = Math.max(answerCardHeight, sourcesCardParentHeight);
      const maxAllowedHeight = window.innerHeight - 250;
      sourcesNavRef.current.style.maxHeight = `${Math.min(maxHeight, maxAllowedHeight)}px`;
    }
  };
  const handleMouseDown = (event: React.MouseEvent) => {
    leftCardWidthRef.current = responseCardRef.current?.offsetWidth || 0;
    rightCardWidthRef.current = sourcesCardParentRef.current?.offsetWidth || 0;
    event.preventDefault(); // Prevent default text selection behavior
    isDragging.current = true;
    // Add hover styling class to the resizer element
    const resizer = document.querySelector('.resizer-h');
    if (resizer) {
      resizer.classList.add('resizing-active');
    }
  };
  const handleMouseUp = () => {
    if (isDragging.current && !isSmallScreen) {
      isDragging.current = false;
      // Remove hover styling class from the resizer element
      const resizer = document.querySelector('.resizer-h');
      if (resizer) {
        resizer.classList.remove('resizing-active');
      }
      // Reset the height of sourcesCardParentRef based on its content
      if (sourcesCardParentRef.current && !isSmallScreen) {
        sourcesCardParentRef.current.style.height = 'auto'; // Let the content determine the height
      }
    }
  };
  const handleMouseMove = useCallback((event: MouseEvent) => {
    event.preventDefault();
    if (isDragging.current && !isSmallScreen) {
      const delta = event.movementX;

      // Define minimum widths for the cards
      const minLeftWidth = 300; // Minimum width for the left card
      // Conditionally set the minimum width for the right card
      const mediaType = sourcesMediaOpen && selectedSourceIndex !== null ? getMediaType(sourcesLocal[selectedSourceIndex], sourcesLocal[selectedSourceIndex]?.sourceInfo) : 'unknown';
      const isAudioOrVideo = mediaType === 'audio' || mediaType === 'video';
      const minRightWidth = isAudioOrVideo ? 730 // Minimum width when mediaDisplay is present
      : 565; // Adjust this value as needed for non-media content

      // Calculate new widths
      leftCardWidthRef.current = fixedWidthCards.answer ? leftCardWidthRef.current || 0 : Math.max(minLeftWidth, (leftCardWidthRef.current || responseCardRef.current?.offsetWidth || 0) + delta || 0);
      rightCardWidthRef.current = fixedWidthCards.sources ? rightCardWidthRef.current || 0 : Math.max(minRightWidth, (rightCardWidthRef.current || sourcesCardParentRef.current?.offsetWidth || 0) - delta || 0);

      // Margin on both sides of the viewport
      const margin = 40;
      // Calculate total width and ensure it doesn't exceed the viewport width
      const totalWidth = leftCardWidthRef.current + rightCardWidthRef.current + 10;
      const maxWidth = window.innerWidth - margin;
      if (totalWidth <= maxWidth) {
        if (!fixedWidthCards.answer) {
          setLeftCardWidth(leftCardWidthRef.current);
        }
        if (!fixedWidthCards.sources && sourcesMediaOpen) {
          setRightCardWidth(rightCardWidthRef.current);
        }
      } else {
        // Adjust widths to fit within the viewport
        const excessWidth = totalWidth - maxWidth;
        if (!fixedWidthCards.answer) {
          setLeftCardWidth(leftCardWidthRef.current - excessWidth / 2);
        }
        if (!fixedWidthCards.sources && sourcesMediaOpen) {
          setRightCardWidth(rightCardWidthRef.current - excessWidth / 2);
        }
      }

      // Reset the height of sourcesCardParentRef to its default
      if (sourcesCardParentRef.current && !isSmallScreen) {
        sourcesCardParentRef.current.style.height = ''; // Reset to default height
      }
    }
  }, [fixedWidthCards, sourcesMediaOpen, sourcesLocal, selectedSourceIndex]);
  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('resize', updateSourcesNavMaxHeight); // Add resize event listener
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('resize', updateSourcesNavMaxHeight); // Clean up resize event listener
    };
  }, [handleMouseMove, handleMouseUp, updateSourcesNavMaxHeight]);
  useEffect(() => {
    const updateSourcesNavHeight = () => {
      if (!isSmallScreen && sourcesNavRef.current && responseCardRef.current) {
        // Get the height of the response card
        const responseCardHeight = responseCardRef.current.offsetHeight;
        // Set the height of sourcesNavRef to match the taller of responseCardRef or sourcesCardParentRef
        if (sourcesMediaOpen) {
          const sourcesCardParentHeight = sourcesCardParentRef.current?.offsetHeight ?? 0;
          const maxHeight = Math.max(responseCardHeight, sourcesCardParentHeight);
          sourcesNavRef.current.style.height = `${maxHeight}px`;
        } else {
          sourcesNavRef.current.style.height = `${responseCardHeight}px`;
        }
      }
    };
    updateSourcesNavHeight();
    window.addEventListener('resize', updateSourcesNavHeight);
    return () => {
      window.removeEventListener('resize', updateSourcesNavHeight);
    };
  }, [sourcesMediaOpen, responseCardRef, isSmallScreen]);
  useEffect(() => {
    if (!isSmallScreen && sourcesMediaOpen && sourcesCardParentRef.current) {
      // Set the height to fit the content
      sourcesCardParentRef.current.style.height = 'auto'; // Reset to content height
    }
  }, [sourcesMediaOpen, isSmallScreen]);
  const restoreSelection = useCallback(() => {
    if (rangeRef.current) {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(rangeRef.current);
      }
    }
  }, []);
  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      dispatchCopyToHistory(text);
      if (rangeRef.current) {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(rangeRef.current);
        }
      }
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
    if (highlightRef.current) {
      highlightRef.current.remove();
      highlightRef.current = null;
    }
    setContextMenu(null);
  };
  const handleSelection = (selection: Selection) => {
    if (selection.toString().length > 0) {
      // Store the selection and range
      selectionRef.current = selection;
      const range = selection.getRangeAt(0);
      rangeRef.current = range;

      // Create highlight element
      if (highlightRef.current) {
        highlightRef.current.remove();
      }

      // Create SVG element
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.classList.add('svg-highlight-answer-item');
      svg.style.position = 'fixed';
      svg.style.top = '0';
      svg.style.left = '0';
      svg.style.width = '100%';
      svg.style.height = '100%';
      svg.style.pointerEvents = 'none';
      svg.style.zIndex = '1';

      // Create path for the highlight
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('fill', 'rgba(84, 126, 173, 0.3)');

      // Create path data from all selection ranges
      let pathData = '';
      let firstRect: DOMRect | null = null;
      let lastRect: DOMRect | null = null;
      let lastLineRect: DOMRect | null = null;
      let lastLineElement: HTMLElement | null = null;

      // Only process the first range to avoid expanding selection
      const rects = Array.from(range.getClientRects()) as DOMRect[];
      rects.forEach((rect, index) => {
        const x = rect.left;
        const y = rect.top;
        const width = rect.width;
        const height = rect.height;
        if (!firstRect) {
          firstRect = rect;
        }
        lastRect = rect;

        // Keep track of the last line's rectangle and element
        if (!lastLineRect || rect.top > lastLineRect.top) {
          lastLineRect = rect;
          // Find the containing line element
          const node = range.commonAncestorContainer;
          const element = node.nodeType === Node.TEXT_NODE ? node.parentElement : node as HTMLElement;
          if (element) {
            // Find the closest block-level element or list item
            let lineElement = element;
            while (lineElement && !lineElement.classList.contains('text-white') && !lineElement.classList.contains('font-light') && !lineElement.classList.contains('text-[14px]') && !lineElement.classList.contains('leading-snug') && !lineElement.tagName.toLowerCase().match(/^(p|li|div)$/)) {
              lineElement = lineElement.parentElement as HTMLElement;
            }
            if (lineElement) {
              lastLineElement = lineElement;
            }
          }
        }
        if (index === 0) {
          pathData += `M ${x} ${y} h ${width} v ${height} h -${width} Z`;
        } else {
          pathData += ` M ${x} ${y} h ${width} v ${height} h -${width} Z`;
        }
      });
      path.setAttribute('d', pathData);
      svg.appendChild(path);
      document.body.appendChild(svg);
      highlightRef.current = svg;

      // Only set context menu if we have a valid last line element and rectangle
      if (lastLineElement && lastLineRect) {
        const rect = lastLineRect as DOMRect;
        setContextMenu({
          x: rect.left + window.scrollX,
          y: rect.bottom + window.scrollY + 10
        });
        setSelectedText(selection.toString());
      }
    }
  };
  useEffect(() => {
    if (!answerItemRef.current) {
      return;
    }
    const handleSelectDown = (e: MouseEvent) => {
      const targetElement = e.target as HTMLElement;
      if (targetElement.closest('.CONTEXT_MENU_REF_CLASS')) {
        return;
      }
      isSelectingRef.current = true;
    };
    const handleSelectUp = (e: MouseEvent) => {
      const targetElement = e.target as any;
      const isInContextMenu = targetElement.closest('.CONTEXT_MENU_REF_CLASS');
      if (isInContextMenu) {
        return;
      }
      isSelectingRef.current = false;
      const selection = window.getSelection();
      if (selection) {
        // Check if this was a triple click
        const clickCount = e.detail;
        if (clickCount === 3) {
          const target = e.target as HTMLElement;

          // Find the closest text block element
          let blockElement = target;
          while (blockElement && !blockElement.classList.contains('text-white') && !blockElement.classList.contains('font-light') && !blockElement.classList.contains('text-[14px]') && !blockElement.classList.contains('leading-snug')) {
            blockElement = blockElement.parentElement as HTMLElement;
          }
          if (blockElement) {
            const range = document.createRange();
            range.selectNodeContents(blockElement);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
        handleSelection(selection);
      }
    };
    const handleSelectClick = (e: MouseEvent) => {
      const contextMenuElement = contextMenuRef.current;
      if (contextMenuElement && !contextMenuElement.contains(e.target as Node)) {
        // Drag-to-select ends with mouseup then a click on the same text; don't dismiss the
        // custom context menu that mouseup just opened.
        if (answerItemRef.current?.contains(e.target as Node) && window.getSelection()?.toString()?.trim()) {
          return;
        }
        setContextMenu(null);
        if (highlightRef.current) {
          highlightRef.current.remove();
          highlightRef.current = null;
        }
      }
    };
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (selection && !isSelectingRef.current) {
        handleSelection(selection);
      }
    };
    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest(`#${questionId}`)) {
        e.preventDefault();
        const selection = window.getSelection();
        if (selection) {
          handleSelection(selection);
        }
      }
    };
    const handleSelctScroll = (e: Event) => {
      // Only hide context menu if scrolling outside of the context menu itself
      const target = e.target as HTMLElement;
      const contextMenuElement = contextMenuRef.current;

      // Check if the scroll event is coming from within the context menu or its dropdown
      if (contextMenuElement && (contextMenuElement.contains(target) || target.closest('.translate-dropdown-container') || target.closest('.custom-context-menu'))) {
        return; // Don't hide context menu if scrolling within it
      }

      // Hide context menu and remove highlight when scrolling elsewhere
      setContextMenu(null);
      if (highlightRef.current) {
        highlightRef.current.remove();
        highlightRef.current = null;
      }
    };
    if (answerItemRef.current.getAttribute('listenersAttacthed') !== 'true') {
      answerItemRef.current.setAttribute('listenersAttacthed', 'true');
      answerItemRef.current.addEventListener('mousedown', handleSelectDown);
      answerItemRef.current.addEventListener('mouseup', handleSelectUp);
      answerItemRef.current.addEventListener('click', handleSelectClick);
      answerItemRef.current.addEventListener('selectionchange', handleSelectionChange);
      answerItemRef.current.addEventListener('contextmenu', handleContextMenu);
    }
    window.addEventListener('scroll', handleSelctScroll, true); // Use capture phase to catch all scroll events

    return () => {
      if (answerItemRef.current) {
        answerItemRef.current.setAttribute('listenersAttacthed', 'false');
        answerItemRef.current.removeEventListener('mousedown', handleSelectDown);
        answerItemRef.current.removeEventListener('mouseup', handleSelectUp);
        answerItemRef.current.removeEventListener('click', handleSelectClick);
        answerItemRef.current.removeEventListener('selectionchange', handleSelectionChange);
        answerItemRef.current.removeEventListener('contextmenu', handleContextMenu);
      }
      window.removeEventListener('scroll', handleSelctScroll, true);
      if (highlightRef.current) {
        highlightRef.current.remove();
      }
    };
  }, [questionId]);
  const handleSendToQuery = async () => {
    if (selectedText && onSendToQuery) {
      setIsExpanded?.(true);
      // Wait for the expansion animation to complete
      await new Promise(resolve => setTimeout(resolve, 750));
      onSendToQuery(`Tell me more about: ${selectedText}`);
      setContextMenu(null);
      // Focus the textarea after a short delay to ensure it's ready
      setTimeout(() => {
        const textarea = document.querySelector('textarea');
        if (textarea) {
          textarea.focus();
        }
      }, 100);
      if (highlightRef.current) {
        highlightRef.current.remove();
        highlightRef.current = null;
      }
      setContextMenu(null);
    }
  };
  const handleTranslateToLanguage = async (language: string) => {
    if (selectedText && onSendToQuery) {
      setIsExpanded?.(true);
      // Wait for the expansion animation to complete
      await new Promise(resolve => setTimeout(resolve, 750));
      onSendToQuery(`Translate the following text to ${language}: ${selectedText}`);
      setContextMenu(null);
      setTranslateDropdownOpen(false);
      // Focus the textarea after a short delay to ensure it's ready
      setTimeout(() => {
        const textarea = document.querySelector('textarea');
        if (textarea) {
          textarea.focus();
        }
      }, 100);
      if (highlightRef.current) {
        highlightRef.current.remove();
        highlightRef.current = null;
      }
      setContextMenu(null);
    }
  };

  // Close translate dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (translateDropdownOpen) {
        const target = event.target as HTMLElement;
        if (!target.closest('.translate-dropdown-container')) {
          setTranslateDropdownOpen(false);
        }
      }
    };
    if (translateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [translateDropdownOpen]);
  useEffect(() => {
    if (selectedSourceIndex !== undefined && selectedSourceIndex !== null) {
      const fullScreenSource = sourcesLocal[selectedSourceIndex];
      setFullScreenSource(fullScreenSource);
    }
  }, [selectedSourceIndex, sourcesLocal]);

  // When changing the selected source, sync fullscreen media viewer's page to the selected source's page.
  // Use functional setter and omit fullscreenMediaItem from deps so multiple AnswerItems don't oscillate
  // (each would otherwise see the other's write as a mismatch and overwrite, causing "Maximum update depth exceeded").
  useEffect(() => {
    if (selectedSourceIndex === null || selectedSourceIndex === undefined || !sourcesLocal[selectedSourceIndex]) return;
    const selectedSource = sourcesLocal[selectedSourceIndex];
    const selectedMediaUrl = selectedSource.previewFilePath && selectedSource.previewFilePath !== selectedSource.sourceFilePath ? selectedSource.previewFilePath : selectedSource.sourceFilePath;
    const pageNum = selectedSource.page != null ? typeof selectedSource.page === 'number' ? selectedSource.page : parseInt(String(selectedSource.page), 10) : 1;
    if (Number.isNaN(pageNum)) return;
    setFullscreenMediaItem((prev: MediaObject | null): MediaObject | null => {
      if (!prev) return prev;
      const fullscreenUrl = prev.mediaUrl ?? prev.originalUrl;
      if (!fullscreenUrl || fullscreenUrl !== selectedMediaUrl) return prev;
      if (prev.mediaContext?.pageNumber === pageNum) return prev;
      return {
        ...prev,
        mediaContext: {
          ...prev.mediaContext,
          pageNumber: pageNum
        }
      };
    });
  }, [selectedSourceIndex, sourcesLocal, setFullscreenMediaItem]);
  useEffect(() => {
    if (answerItemRef.current) {
      const mimicCopy = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && (event.key === 'c' || event.key === 'C')) {
          handleCopy(selectedText);
        }
      };
      document.addEventListener('keydown', mimicCopy);
      return () => {
        document.removeEventListener('keydown', mimicCopy);
      };
    }
  }, [selectedText]);
  return <div id={questionId} ref={answerItemRef} className={`z-1 box-border flex flex-col max-w-[90%] select-text overflow-y-auto custom-scrollbar2 lg:max-w-auto lg:flex-row
        flex-grow justify-center bg-white/5 lg:bg-white/0 border-1 border-solid border-white/5 m-4 rounded-md p-0 lg:p-3
        ${isSmallScreen ? '' : 'h-[calc(100vh-200px)] max-h-[calc(100vh-200px)]'}
        ${sourcesMediaOpen ? 'gap-2' : 'gap-3'}
      `} style={{
    userSelect: 'text',
    WebkitUserSelect: 'text'
  }} onContextMenu={e => {
    e.preventDefault();
    e.stopPropagation();
    const selection = window.getSelection();
    if (selection) {
      handleSelection(selection);
    }
  }} data-sentry-component="AnswerItem" data-sentry-source-file="answer-item.tsx">
      {contextMenu && <div ref={contextMenuRef} className="CONTEXT_MENU_REF_CLASS fixed bg-[#2C3A4A] rounded p-1 z-[1000] shadow-lg flex flex-row gap-2 custom-context-menu" style={{
      left: contextMenu.x,
      top: contextMenu.y
    }} onMouseDown={e => {
      e.stopPropagation();
      return false;
    }} onMouseUp={e => {
      e.stopPropagation();
      return false;
    }} onClick={e => {
      e.stopPropagation();
      restoreSelection();
    }}>
          <Tooltip content="Copy" delay={500} offset={6} showArrow={true} classNames={{
        content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
        base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
      }}>
            <button onMouseDown={e => {
          e.stopPropagation();
          return false;
        }} onMouseUp={e => {
          e.stopPropagation();
          return false;
        }} onClick={e => {
          e.stopPropagation();
          handleCopy(selectedText);
        }} className="flex items-center gap-2 w-full px-1 py-1 border-none bg-transparent text-white
                brightness-50 hover:brightness-0 invert hover:cursor-pointer text-sm text-left rounded-sm hover:bg-white/5">
              <CopyIcon alt='Copy' size={20} weight='light' className='brightness-50 hover:brightness-0 invert hover:cursor-pointer' />
            </button>
          </Tooltip>
          <div className="relative translate-dropdown-container">
            <Tooltip content="Translate into..." delay={500} offset={6} showArrow={true} classNames={{
          content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
          base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
        }}>
              <button onMouseDown={e => {
            e.stopPropagation();
            return false;
          }} onMouseUp={e => {
            e.stopPropagation();
            return false;
          }} onClick={e => {
            e.stopPropagation();
            setTranslateDropdownOpen(!translateDropdownOpen);
          }} className="flex items-center gap-1 w-full pl-1 pr-0 py-1 border-none bg-transparent text-white brightness-50
                  hover:brightness-0 invert hover:cursor-pointer text-sm text-left rounded-sm hover:bg-white/5">
                <TranslateIcon alt='Translate' size={20} weight='light' className='brightness-60 hover:brightness-0 hover:cursor-pointer' />
                <CaretDownIcon alt='Translate Language Menu' size={12} className='brightness-40 hover:brightness-0 hover:cursor-pointer' />
              </button>
            </Tooltip>
            
            {translateDropdownOpen && <div className="box-border absolute top-full left-0 mt-[5px] bg-[#2C3A4A] rounded-md shadow-lg border border-white/10
                  z-[1001] min-w-[95px] max-h-[110px] overflow-x-hidden overflow-y-auto custom-scrollbar2" onMouseDown={e => {
          e.stopPropagation();
          return false;
        }} onMouseUp={e => {
          e.stopPropagation();
          return false;
        }} onClick={e => e.stopPropagation()}>
                {languageOptions.map(language => <Tooltip key={language.code} content={language.englishName} delay={1000} offset={6} placement="left" showArrow={true} classNames={{
            content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
            base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
          }}>
                    <button onMouseDown={e => {
              e.stopPropagation();
              return false;
            }} onMouseUp={e => {
              e.stopPropagation();
              return false;
            }} onClick={e => {
              e.stopPropagation();
              handleTranslateToLanguage(language.name);
            }} className="box-border w-full px-1 py-1 text-left text-white text-sm hover:bg-white/10 first:rounded-t-md last:rounded-b-md hover:cursor-pointer">
                      {language.name}
                    </button>
                  </Tooltip>)}
              </div>}
          </div>

          <Tooltip content="Tell me more" showArrow={true} delay={500} offset={6} classNames={{
        content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
        base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
      }}>
            <button onMouseDown={e => {
          e.stopPropagation();
          return false;
        }} onMouseUp={e => {
          e.stopPropagation();
          return false;
        }} onClick={e => {
          e.stopPropagation();
          handleSendToQuery();
        }} className="flex items-center gap-2 w-full px-1 py-1 border-none bg-transparent text-white brightness-50 hover:brightness-0
                invert hover:cursor-pointer text-sm text-left rounded-sm hover:bg-white/5">
              <Image className='w-[20px] h-[20px] hover:cursor-pointer' alt='Tell me more.' src='/icons/assistant.svg' width='25' height='25' />
            </button>
          </Tooltip>
        </div>}

      <HotKeys onCloseModal={closeSources} isModal={sourcesMediaOpen} data-sentry-element="HotKeys" data-sentry-source-file="answer-item.tsx" />
      <Card ref={responseCardRef} style={{
      width: leftCardWidth || '33%',
      flexGrow: leftCardWidth ? 0 : 1,
      minWidth: isSmallScreen ? '100%' : sourcesMediaOpen ? '300px' : '300px',
      maxWidth: isSmallScreen ? '80%' : '100%',
      cursor: fixedWidthCards.answer ? 'default' : 'auto',
      height: 'auto',
      alignSelf: 'flex-start',
      overflowX: 'hidden'
    }} classNames={{
      base: 'h-[calc(100vh-227px)] max-h-[calc(100vh-227px)] box-border flex bg-transparent shadow-lg border-[1px] border-white/5 border-solid bg-black/10 rounded-md m-0 p-0',
      header: 'box-border flex flex-row gap-3 items-start m-0 px-2 lg:px-4 rounded-none border-b-[1px] border-solid border-white/5 bg-white/5',
      body: 'box-border flex flex-col items-start m-0 p-2 lg:p-4 bg-black/15 selectable-text',
      footer: 'box-border min-h-[45px] flex flex-row gap-2 items-center m-0 rounded-none px-2 py-2 m-0 bg-white/5 border-t-[1px] border-solid border-white/5 '
    }} data-sentry-element="Card" data-sentry-source-file="answer-item.tsx">
        <CardHeader data-sentry-element="CardHeader" data-sentry-source-file="answer-item.tsx">
          <a href={`#${questionId}`} className="text-blue-100 no-underline cursor-default">
            <h3 className='flex max-h-[90px] overflow-y-auto custom-scrollbar2 m-0 p-0 pr-2 font-light text-xl lg:text-2xl italic'>{question}</h3>
          </a>
          <div>
            <Tooltip content={redoClicked ? 'Question sent to Assistant' : 'Ask question again'} delay={500} offset={6} isOpen={tooltipOpen} onOpenChange={setTooltipOpen} showArrow={true} classNames={{
            content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
            base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
          }} data-sentry-element="Tooltip" data-sentry-source-file="answer-item.tsx">
              <button data-testid="RedoQuestion" aria-label={redoClicked ? "Question sent to Assistant" : "Ask question again"} className={`h-fit w-fit btn-h bg-transparent transition-colors duration-300 rounded-sm p-0 cursor-pointer [&>*]:cursor-pointer
                ${redoClicked ? 'bg-brand-blue hover:bg-brand-blue' : 'hover:bg-tooltip-bg'}`} onClick={event => {
              handleRedoClick();
              recordStat('RedoQuestionClicked', event, statsContext, !!featureFlags?.useIntercom);
            }}>
                {redoClicked ? <CheckIcon size={25} className="p-[5px]" /> : <Image className='w-[25px] h-[25px] p-[5px] brightness-50 hover:brightness-0 invert hover:cursor-pointer' alt='Ask question again.' src='/icons/assistant.svg' width='25' height='25' />}
              </button>
            </Tooltip>
          </div>
          {scopesObject && (scopesObject.scopes?.length > 0 || scopesObject.files?.length > 0 || scopesObject.excludedScopes?.length > 0 || scopesObject.excludedFiles?.length > 0) && <div>
            <Tooltip content="Apply scopes from this query" delay={500} offset={6} isOpen={tooltip3Open} onOpenChange={setTooltip3Open} showArrow={true} classNames={{
            content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
            base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
          }}>
              <button data-testid="ApplyScopes" className={`h-fit w-fit btn-h bg-transparent transition-colors duration-300 rounded-sm p-0 cursor-pointer [&>*]:cursor-pointer
                ${applyScopesClicked ? 'bg-brand-blue hover:bg-brand-blue' : 'hover:bg-tooltip-bg'}`} onClick={event => {
              handleApplyScopesClick();
              recordStat('ApplyScopesClicked', event, statsContext, !!featureFlags?.useIntercom);
            }}>
                {applyScopesClicked ? <CheckIcon size={25} className="p-[5px]" /> : <FunnelIcon size={25} className="p-[5px]" />}
              </button>
            </Tooltip>
          </div>}
        </CardHeader>
        <CardBody data-sentry-element="CardBody" data-sentry-source-file="answer-item.tsx">
          <div className='overflow-y-auto custom-scrollbar2 w-[calc(100%-1px)] h-[100%]' onScroll={localSnapToTop}>
            {isRefusal ? <div className='flex flex-col lg:flex-row items-start gap-2 p-4'>
                <div className='text-[14px] lg:text-lg'>
                  {answerTextLocal}
                </div>
                <Popover>
                  <Tooltip content="Learn more" delay={500} offset={6} showArrow={true} classNames={{
                content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
                base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
              }}>
                    <PopoverTrigger asChild>
                      <button type="button" aria-label="Information about this response" aria-haspopup="dialog" className="mt-[-20px] ml-[5px] lg:mt-0 lg:ml-0 h-fit w-fit btn-h bg-transparent transition-colors duration-300 rounded-sm p-1 hover:bg-tooltip-bg cursor-pointer [&>*]:cursor-pointer">
                        <InfoIcon size={20} weight="duotone" />
                      </button>
                    </PopoverTrigger>
                  </Tooltip>
                  <PopoverContent className='bg-[#2C3A4A]'>
                    <div className='flex flex-row m-0 pb-2 gap-2 align-top border-b-[.5px] border-[hsla(0,0%,100%,0.9)] border-solid'>
                      <InfoIcon size={40} weight="light" />
                      <h3 className='m-0 p-0 italic font-normal'>&ldquo;Sorry, I am unable to assist you with this request.&rdquo;</h3>
                    </div>
                    <p>TimePilot <span className="ml-[3px] mt-[-2px] text-[9px] font-thin not-italic">&reg;</span> will sometimes reply, &ldquo;Sorry, I am unable to assist you with this request.&rdquo; This usually has two causes: either TimePilot <span className="ml-[3px] mt-[-2px] text-[9px] font-thin not-italic">&reg;</span> couldn&rsquo;t find any information related to the question or the question may have been too vague or broad.</p>
                    <p>We are working to minimize occurrences of this response and to provide more details when an answer cannot be provided, but in the meantime please provide feedback using the button in the lower-right corner of the app when you encounter this along with the case and question asked.</p>
                    <p>Common question rewording that can help when encountering a refusal:</p>
                    <ol>
                      <li>Include &ldquo;possible&rdquo;, for example &ldquo;List possible school shooters&rdquo;</li>
                      <li>Try synonyms like &ldquo;threats&rdquo; instead of &ldquo;extremists&rdquo;</li>
                      <li>Use the past tense instead of present (e.g. use &ldquo;idolized&rdquo; instead of &ldquo;idolizes&rdquo;)</li>
                    </ol>
                    <p>Here are some additional general suggestions for asking questions:</p>
                    <ol>
                      <li>Be specific and clear in your questions. The more precise you are, the better TimePilot <span className="ml-[3px] mt-[-2px] text-[9px] font-thin not-italic">&reg;</span> can provide relevant information.</li>
                      <li>Provide context if necessary. If your question relates to a specific situation or background information, include that to get more accurate answers.</li>
                      <li>Be patient and prepared to ask follow-up questions. Sometimes it may take a few exchanges to get to the information you&rsquo;re seeking.</li>
                      <li>If you&rsquo;re not satisfied with an answer, try rephrasing your question or breaking it down into smaller, more specific queries.</li>
                    </ol>
                  </PopoverContent>
                </Popover>
              </div> : <div id={`answerText_${answerIndex}`} className="h-[100%]">
                <div className="h-[100%]">
                  {!answerTextLocal || answerTextLocal.trim() === '' || answerTextLocal.startsWith('PRELIMINARY_MESSAGES|') ? <div className="flex flex-row w-[calc(100%-10px)] h-full min-h-[100px] ml-[4px]">
                      <div onScroll={localSnapToTop} ref={preliminaryMessagesRef} className="w-[calc(100%-10px)] flex flex-col gap-0 text-white font-light text-[14px] leading-snug m-0 overflow-y-auto custom-scrollbar2" aria-live="polite" role="status">
                        {(!answerTextLocal || answerTextLocal.trim() === '' ? FETCHING_MESSAGE : answerTextLocal.slice('PRELIMINARY_MESSAGES|'.length)).split('\n').map((message: string, index: number) => <div key={index} className="box-border w-full h-fit flex flex-row gap-0 items-center text-white font-light text-[14px] leading-snug m-0 min-h-fit">
                            <div className="min-w-[31px] flex flex-row items-center justify-start p-0">
                              {index === (!answerTextLocal || answerTextLocal.trim() === '' ? FETCHING_MESSAGE.split('\n').length : answerTextLocal?.slice('PRELIMINARY_MESSAGES|'.length).split('\n').length) - 1 ? <div className="relative z-1 flex flex-row gap-2 items-start justify-start w-full min-h-[32px]">
                                  <div className="w-[27px] h-[27px] p-[3px] box-border flex items-center justify-center bg-[#0C1B2E] border-[1px] border-white border-solid rounded-full">
                                    <Spinner variant="spinner" size="sm" className="[&_i]:bg-blue-300" />
                                  </div>
                                  <div className="box-border flex font-light w-full text-[14px] leading-snug min-h-[32px] mt-[4px] text-blue-300">
                                    {message === "Analysis complete." ? "Analysis complete, the answer will be displayed in a moment." : message}
                                  </div>
                                </div> : <div className="relative z-1 flex flex-row-reverse gap-2 items-start justify-start w-full min-h-[21px]">
                                  <div className="relative z-1 box-border flex font-light w-full text-[14px] leading-snug pb-[10px] min-h-[32px] text-white
                                    pl-[20px] -ml-[19px] mt-[2px] border-l-[1px] border-white border-solid">
                                    {message}
                                  </div>
                                  <div className="relative z-2 box-border flex items-center justify-start w-[21px] h-[21px] max-h-[21px] min-h-[21px]
                                      p-[3px] ml-[3px] bg-[#0C1B2E] border-[1px] border-white border-solid rounded-full">
                                    <CheckIcon size={15} weight="bold" className="relative z-3 text-green-500" />
                                  </div>
                                </div>}
                            </div>
                          </div>)}
                      </div>
                    </div> : <ReactMarkdown components={{
                h1: ({
                  node,
                  ...props
                }) => <h1 className="text-blue-400 font-semibold text-lg lg:text-2xl border-b-[2px] border-solid border-blue-200 m-0 p-0 mt-1 mb-3 pb-0.5" {...props} />,
                h2: ({
                  node,
                  ...props
                }) => <h2 className="text-blue-300 font-semibold text-base lg:text-base m-0 p-0 pt-1 pb-2" {...props} />,
                h3: ({
                  node,
                  ...props
                }) => <h3 className="text-blue-200 font-medium text-sm italic" {...props} />,
                ul: ({
                  node,
                  ...props
                }) => <StyledList {...props} />,
                ol: ({
                  node,
                  ...props
                }) => <StyledList ordered {...props} />,
                li: ({
                  node,
                  ...props
                }) => <StyledListItem {...props} />,
                a: ({
                  node,
                  children
                }) => <span>{children}</span>,
                p: ({
                  node,
                  children
                }) => {
                  // Check if children is empty or only contains whitespace
                  const isEmpty = !children || Array.isArray(children) && children.every(child => typeof child === 'string' && child.trim() === '');
                  return isEmpty ? <p className="hidden" /> // Empty paragraph
                  : <p className="text-white font-light text-[14px] leading-snug m-0 pb-2 lg:max-w-[800px]">
                              {children}
                            </p>;
                },
                sup: ({
                  node,
                  children
                }) => <sup className="text-white bg-[#2C3A4A] text-[9px] leading-[14px] rounded-full ml-0.5 min-w-[14px] min-h-[14px] inline-block align-middle text-center whitespace-nowrap">
                            {children}
                          </sup>
              }} rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
                      {answerTextLocal}
                    </ReactMarkdown>}
                </div>
              </div>}
          </div>
          <div className="absolute bottom-[1px] right-[1px] w-[1px] h-[1px] bg-black overflow-hidden">
            <div ref={answerItemMarkdownRef} className="flex flex-row items-start justify-start">
              {isExporting && <div className="flex flex-col items-start justify-start gap-1 text-black">
                  <div className="w-full mb-3 pb-1">
                    <p className="text-blue-600 font-semibold text-[12px] uppercase tracking-wider m-0 pb-1">Question</p>
                    <p className="text-black font-medium text-[14px] leading-snug m-0">{question}</p>
                  </div>
                  <ReactMarkdown components={{
                h1: ({
                  node,
                  ...props
                }) => <h1 className="text-blue-600 font-semibold text-lg lg:text-2xl border-b-[2px] border-solid border-blue-200 m-0 p-0 mt-1 mb-3 pb-0.5" {...props} />,
                h2: ({
                  node,
                  ...props
                }) => <h2 className="text-blue-500 font-semibold text-base lg:text-base m-0 p-0 pt-1 pb-2" {...props} />,
                h3: ({
                  node,
                  ...props
                }) => <h3 className="text-blue-400 font-medium text-sm italic" {...props} />,
                ul: ({
                  node,
                  ...props
                }) => <StyledList className="text-black" {...props} />,
                ol: ({
                  node,
                  ...props
                }) => <StyledList ordered className="text-black" {...props} />,
                li: ({
                  node,
                  ...props
                }) => <StyledListItem className="text-black" {...props} />,
                a: ({
                  node,
                  children
                }) => <span className="text-black">{children}</span>,
                p: ({
                  node,
                  children
                }) => {
                  // Check if children is empty or only contains whitespace
                  const isEmpty = !children || Array.isArray(children) && children.every(child => typeof child === 'string' && child.trim() === '');
                  return isEmpty ? <p className="hidden" /> // Empty paragraph
                  : <p className="text-black font-light text-[14px] leading-snug m-0 pb-2">
                            {children}
                          </p>;
                },
                sup: ({
                  node,
                  children
                }) => <sup className="text-black bg-[#dddddd] text-[9px] leading-[14px] rounded-full ml-0.5 min-w-[14px] min-h-[14px] inline-block align-middle text-center whitespace-nowrap">
                          {children}
                        </sup>
              }} rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
                    {answerTextLocal}
                  </ReactMarkdown>
                  {sourcesLocal.length > 0 && <div className="flex flex-col items-start justify-start gap-2">
                      <div className="text-blue-400 font-semibold text-[14px] leading-snug m-0 mt-2 pb-2">Sources:</div>
                      {sourcesLocal.map((source, index) => {
                  const sourceMediaType = sourcesMediaOpen ? getMediaType(source, source.sourceInfo) : null;
                  const {
                    showPageNumber,
                    timeSuffix
                  } = sourceListLabelParts(source, sourceMediaType);
                  let sourceText = `${getFilename(source)}${showPageNumber ? `, page ${source.page}` : ''}${timeSuffix}`;
                  if (source.sourceFilePath?.endsWith('.mbox') || source.sourceFilePath?.endsWith('.mbx')) {
                    if (!!source.analysisFilePath && source.analysisFilePath !== source.sourceFilePath) {
                      const attachmentFileName = source.analysisFilePath.split('/').pop();
                      if (!!attachmentFileName && attachmentFileName.trim() !== '') {
                        sourceText += `, ${attachmentFileName}`;
                      }
                    }
                  }
                  return <div key={source.index} className="flex flex-row items-start justify-start gap-2">
                            <div className="w-[21px] h-[21px] min-w-[21px] min-h-[21px] box-border bg-[#dddddd] text-black rounded-full text-[10px] flex items-center justify-center">{index + 1}</div>
                            <div className="flex flex-row items-start justify-start break-all text-[14px]">{sourceText}</div>
                          </div>;
                })}
                    </div>}
                </div>}
            </div>
          </div>
        </CardBody>
        {!isRefusal && <CardFooter className="flex flex-row justify-between">
            <div className="flex flex-row gap-1">
              <Tooltip content={copied ? 'Copied!' : 'Copy response'} delay={500} offset={6} showArrow={true} classNames={{
            content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
            base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
          }}>
                <button data-testid="CopyResponse" className={`id-CopyResponse h-fit w-fit btn-h bg-transparent transition-colors duration-300 rounded-sm p-1 cursor-pointer [&>*]:cursor-pointer
                    ${copied ? 'bg-brand-blue hover:bg-brand-blue' : 'hover:bg-tooltip-bg'}`} onClick={event => {
              handleCopyClick(question, answerIndex, sourcesLocal, answerTextLocal || '');
              recordStat('CopyResponseClicked', event, statsContext, !!featureFlags?.useIntercom);
            }}>
                  {copied ? <CheckIcon size={20} /> : <CopyIcon size={20} />}
                </button>
              </Tooltip>
              <Tooltip content="Create timeline event from response" delay={500} offset={6} showArrow={true} classNames={{
            content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
            base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
          }}>
                <button data-testid="CreateTimelineEventFromResponse" className="h-fit w-fit btn-h bg-transparent transition-colors duration-300 rounded-sm p-1 cursor-pointer [&>*]:cursor-pointer hover:bg-tooltip-bg" onClick={event => {
              addToTimeline({
                answerText: answerTextLocal,
                sources: sourcesLocal,
                autogen: false,
                epochTimeSecs: epochTimeSecs
              } as EventFromChat);
              recordStat('CreateTimelineEventFromResponseClicked', event, statsContext, !!featureFlags?.useIntercom);
            }}>
                  <ListPlusIcon size={20} />
                </button>
              </Tooltip>
              <Tooltip content={wandDisabled || (extractionJobStatus === 'pending' || extractionJob?.status === 'pending') && timelineReady ? 'Event extraction job in progress, please wait for it to complete before generating more events' : canApplyJob ? 'View extracted events' : 'Generate timeline events'} delay={500} offset={6} showArrow={true} classNames={{
            content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
            base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
          }} className="max-w-[200px] text-center">
                <button data-testid="GenerateTimelineEvents" className={`h-fit w-fit btn-h bg-transparent transition-colors duration-300 rounded-sm p-1
                     ${wandDisabled || (extractionJobStatus === 'pending' || extractionJob?.status === 'pending') && timelineReady ? 'opacity-50 cursor-not-allowed [&>*]:cursor-not-allowed' : 'cursor-pointer [&>*]:cursor-pointer hover:bg-tooltip-bg'}`} onClick={event => {
              if (wandDisabled || (extractionJobStatus === 'pending' || extractionJob?.status === 'pending') && timelineReady) {
                return;
              } else if (canApplyJob && (extractionJobStatus === 'completed' || extractionJob?.status === 'completed')) {
                setApplyCardOpen(true);
              } else {
                setExtractionJobStatus('pending');
                addToTimeline({
                  answerText: answerTextLocal,
                  sources: sourcesLocal,
                  autogen: true,
                  epochTimeSecs: epochTimeSecs
                } as EventFromChat);
              }
              recordStat('GenerateTimelineEventsClicked', event, statsContext, !!featureFlags?.useIntercom);
            }}>
                  {(extractionJobStatus === 'pending' || extractionJob?.status === 'pending') && timelineReady ? <Spinner className="-mt-[3px] mb-[3px]" variant="wave" size="sm" color="white" /> : <>
                      {canApplyJob && timelineReady ? <CheckIcon size={20} className="text-green-700" /> : <MagicWandIcon size={20} />}
                    </>}
                </button>
              </Tooltip>
              {featureFlags?.exportAnswerAsPdf && <Tooltip content="Download PDF of this response" delay={500} offset={6} showArrow={true} classNames={{
            content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
            base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
          }}>
                  <button data-testid="DownloadPDFOfResponse" className="mt-[1px] h-fit w-fit btn-h bg-transparent transition-colors duration-300 rounded-sm p-1 cursor-pointer [&>*]:cursor-pointer hover:bg-tooltip-bg" onClick={event => {
              if (answerItemMarkdownRef.current && !isExporting) {
                handleExportAnswerAsPdf(epochTimeSecs, answerItemMarkdownRef.current);
                recordStat('DownloadPDFOfResponseClicked', event, statsContext, !!featureFlags?.useIntercom);
              }
            }}>
                    {isExporting ? <Spinner className="-mt-[3px] mb-[3px]" variant="wave" size="sm" color="white" /> : <FilePdfIcon size={20} />}
                  </button>
                </Tooltip>}
              {canApplyJob && <EventsReviewModal isOpen={applyCardOpen} onClose={() => setApplyCardOpen(false)} question={question} extractionJob={extractionJob} setApplyCardOpen={setApplyCardOpen} createEventsFromEntityEvents={createEventsFromEntityEvents} getEventsFromEntityService={getEventsFromEntityService} />}
            </div>
            {resultForFeedback?.chat_key && showFeedbackModal && <Modal isOpen={showFeedbackModal} onClose={cancelChatResultFeedback} classNames={{
          wrapper: 'bg-black/50',
          base: 'bg-black/50 h-[calc(50vh)] lg:max-w-[450px] max-w-[90vw] overflow-y-auto rounded-md',
          backdrop: 'bg-black/50',
          header: 'bg-gray-600 text-white font-light',
          body: 'bg-white text-black py-5 overflow-y-auto pb-[100px]',
          closeButton: 'flex items-center justify-center rounded-md bg-white/10 hover:bg-white/20 text-white cursor-pointer',
          footer: 'absolute bottom-0 left-0 right-0 bg-gray-600 text-white font-light'
        }}>
                <ModalContent>
                  <ModalHeader>
                    Chat Result {positiveFeedbackClicked ? 'Positive' : 'Negative'} Feedback Form
                  </ModalHeader>
                  <ModalBody>
                    <h3 className="text-black font-medium m-0 p-0">
                      Feedback:
                    </h3>
                    <textarea aria-label="Feedback details" className="box-border text-black border-1 border-solid border-black/50 rounded-md p-2 min-h-[200px] font-inter w-full" value={feedbackReason} placeholder="Please provide details about how TimePilot's response can be improved." onChange={e => setFeedbackReason(e.target.value)} />
                    <div className="text-black">
                      <h3 className="font-medium">
                        Question asked:
                      </h3>
                      <div className="box-border text-black/70 font-mono border-1 border-solid border-black/50 rounded-md p-2 w-full italic bg-black/5">{question}</div>
                    </div>
                    <div className="text-black">
                      <h3 className="font-medium">
                        Answer provided:
                      </h3>
                      <div className="box-border text-black/70 font-mono border-1 border-solid border-black/50 rounded-md p-2 w-full italic bg-black/5 max-h-[100px] overflow-y-auto custom-scrollbar2">
                        {answerTextLocal}
                      </div>
                    </div>
                  </ModalBody>
                  <ModalFooter>
                    <Button onPress={cancelChatResultFeedback} className="bg-gray-600 text-white font-light hover:bg-gray-700 cursor-pointer border-1 border-solid border-black/50">
                      Cancel
                    </Button>
                    <Button onPress={sendChatResultFeedback} className="bg-brand-blue text-white/80 hover:text-white font-light hover:bg-brand-blue-light cursor-pointer">
                      Submit Feedback
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>}
            {resultForFeedback?.chat_key && <div className="flex flex-row gap-1">
                {resultForFeedback?.chat_key && !feedbackSubmitted && <Tooltip content="Positive feedback" delay={500} offset={6} showArrow={true} classNames={{
            content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
            base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
          }}>
                    <button className="btn-h bg-transparent transition-colors duration-300 rounded-sm p-1 cursor-pointer [&>*]:cursor-pointer hover:bg-tooltip-bg" onClick={() => handleFeedbackClick('positive')}>
                      <ThumbsUpIcon size={20} />
                    </button>
                  </Tooltip>}
                {resultForFeedback?.chat_key && !feedbackSubmitted && <Tooltip content="Negative feedback" delay={500} offset={6} showArrow={true} classNames={{
            content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
            base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
          }}>
                    <button className="btn-h bg-transparent transition-colors duration-300 rounded-sm p-1 cursor-pointer [&>*]:cursor-pointer hover:bg-tooltip-bg" onClick={() => handleFeedbackClick('negative')}>
                      <ThumbsDownIcon size={20} />
                    </button>
                  </Tooltip>}
                {resultForFeedback?.chat_key && feedbackSubmitted && <div className="flex flex-row cursor-default text-white/70">
                    Feedback Submitted
                  </div>}
              </div>}
          </CardFooter>}
      </Card>

      {sourcesLocal?.length > 0 && (!fixedWidthCards.answer || !fixedWidthCards.sources) && sourcesMediaOpen && !isSmallScreen && <div className='resizer-h cursor-col-resize' onMouseDown={handleMouseDown} style={{
      width: '10px',
      cursor: 'col-resize'
    }}>
          <button className='h-[20px]'></button>
          <CaretCircleUpDownIcon size={18} weight='fill' className="transform rotate-90" />
          <button></button>
        </div>}
      {sourcesLocal?.length > 0 && <Card ref={sourcesCardParentRef} style={{
      width: rightCardWidth || '67%',
      flexGrow: rightCardWidth ? 0 : 1,
      minWidth: isSmallScreen ? '100%' : sourcesMediaOpen ? '700px' : '300px',
      maxWidth: sourcesMediaOpen ? '100%' : '260px',
      cursor: fixedWidthCards.sources ? 'default' : 'auto',
      height: sourcesMediaOpen ? '' : 'auto',
      alignSelf: 'flex-start',
      overflowX: 'hidden'
    }} classNames={{
      base: `box-border flex flex-col lg:flex-wrap items-start bg-white/5 shadow-lg p-0 m-0 rounded-md overflow-x-hidden h-full
              ${sourcesMediaOpen ? 'lg:min-h-[200px] lg:flex-grow ' : 'w-fit'} ${isSmallScreen ? 'items-center' : ''}`,
      body: `box-border flex flex-col lg:flex-row gap-2 m-0 p-0 rounded-md`
    }}>
          <CardBody className={`overflow-x-hidden' : ''}`}>
            <Card ref={sourcesNavRef} classNames={{
          base: `box-border bg-transparent flex flex-col items-center border-1 border-solid border-white/0 shadow-none overflow-y-auto p-1 lg:p-4
                rounded-sm min-w-full lg:min-h-[300px] lg:min-w-[300px] ${isSmallScreen ? 'max-h-[200px]' : 'lg:max-h-[calc(100vh-150px)]'} `,
          header: 'box-border flex flex-row gap-2 items-center p-0 m-0 pb-1 mb-2 rounded-none border-b-[3px] border-white border-double',
          body: 'box-border m-0 lg:mr-[-10px] p-0 lg:pr-[10px] custom-scrollbar2 overflow-y-auto'
        }}>
              <CardHeader>
                <ArchiveIcon size={25} weight="light" />
                <h4 className='m-0 p-0 font-light text-lg'>Sources</h4>
              </CardHeader>
              <CardBody>
               <div className='max-h-[calc(100vh-304px)] overflow-y-auto custom-scrollbar2' onScroll={localSnapToTop}>
                  <ol className="flex flex-col gap-1 counter-list list-none m-0 p-0 pb-2 border-b-[3px] border-double border-white">
                    {sourcesLocal.map((source, index) => {
                  const sourceMediaType = sourcesMediaOpen ? getMediaType(source, source.sourceInfo) : null;
                  const {
                    showPageNumber,
                    timeSuffix
                  } = sourceListLabelParts(source, sourceMediaType);
                  let attachementFileName = null;
                  let emailFileName = null;
                  if (source.sourceFilePath?.endsWith('.mbox') || source.sourceFilePath?.endsWith('.mbx')) {
                    if (source.previewFilePath) {
                      emailFileName = source.previewFilePath.split('/').pop();
                    }
                    if (!!source.analysisFilePath && source.analysisFilePath !== source.sourceFilePath) {
                      attachementFileName = source.analysisFilePath.split('/').pop();
                    }
                  }
                  return <React.Fragment key={index}>
                          <li id={`source_${source.index}`} data-testid="OpenSources" key={source.index} className={`max-w-[calc(100%-1px)] counter-item break-words m-0 pt-[10px] pr-0 pb-[11px] pl-[9px] flex hover:shadow-inner
                              hover:bg-white/5 rounded-md items-center justify-start text-white/70 hover:text-white cursor-pointer
                              before:content-[counter(source-counter)] before:counter-increment-[source-counter] before:w-[17px]
                              before:h-[17px] before:min-w-[17px] before:min-h-[17px] before:bg-[#2C3A4A] before:text-white
                              before:rounded-[10px] before:border before:border-transparent before:flex before:items-center
                              before:justify-center before:text-[10px] before:mr-[8px]
                              ${selectedSourceIndex === index ? 'before:border-1 before:border-solid before:border-white bg-white/5 border-[1px] border-solid border-white/0' : 'before:border-1 before:border-solid before:border-white/0 border-[1px] border-dashed border-white/5'}
                            `} onClick={event => {
                      openSources(index);
                      recordStat('OpenSourcesClicked', event, statsContext, !!featureFlags?.useIntercom);
                    }}>
                            <div className={`box-border flex flex-row gap-2 items-center justify-between px-1 rounded-sm cursor-pointer ${selectedSourceIndex === index ? 'text-white' : ''}`}>
                              <label className={`flex-1 pr-[10px] text-[12px] max-w-[205px] cursor-pointer answerSource_${answerIndex}`} onClick={() => openSources(index)}>
                                <span className="break-all">
                                    {getFilename(source)}
                                    {showPageNumber ? `, page ${source.page}` : ''}
                                    {timeSuffix}
                                    {emailFileName ? `, ${emailFileName}` : ''}
                                    {attachementFileName ? `, ${attachementFileName}` : ''}
                                </span>
                              </label>
                            </div>
                          </li>
                        </React.Fragment>;
                })}
                  </ol>
                </div>
              </CardBody>
            </Card>

            {sourcesMediaOpen && selectedSourceIndex !== null && <div className='max-h-[calc(100vh-250px)] pt-[10px] overflow-y-auto custom-scrollbar2' onScroll={localSnapToTop}>
                <SourceCard sources={sourcesLocal} selectedSourceIndex={selectedSourceIndex} answerIndex={answerIndex} epochTimeSecs={epochTimeSecs} handleDownload={handleDownload} handleAddSourceToScope={handleAddSourceToScope} handleCopySourceForTimeline={handleCopySourceForTimeline} handleCopyToMediaShare={mediaSourceShare?.addToMediaShare ? (source: Source, _index: number, onSuccess?: () => void) => mediaSourceShare.addToMediaShare(source, onSuccess) : undefined} setIsExpanded={setIsExpanded} fullScreenSource={fullScreenSource} copiedSource={copiedSource} addedSource={addedSource} closeSources={closeSources} addToTimeline={addToTimeline} />
              </div>}
          </CardBody>
        </Card>}
    </div>;
};